import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { bool, func } from 'prop-types';
import React, { useEffect, useState } from 'react';
import KnowledgeFolderSelector from 'components/@home/@settings/Tabs/Agents/KnowledgeFolderSelector';
import KnowledgeApi from 'services/api/Knowledge';
import agenticSlug from 'utils/agenticSlug';
import { I18n } from 'utils/i18n';

export function knowledgeCreateError(err) {
  const fields = [].concat(err?.data?.error?.key || []);
  if (err?.status === 409 && fields.includes('folderId')) {
    return I18n.t('AgenticChatFlow.KnowledgeFolderInUse');
  }
  if (err?.status === 409) {
    return I18n.t('AgenticChatFlow.KnowledgeNameInUse');
  }
  return I18n.t('AgenticChatFlow.CreateKnowledgeFailed');
}

const CreateKnowledgeDialog = ({ open, onClose, onCreated }) => {
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', description: '', folderId: '' });

  useEffect(() => {
    if (!open) return;
    setForm({ name: '', description: '', folderId: '' });
    setError('');
    setLoadingFolders(true);
    KnowledgeApi.listFolderOptions()
      .then(list => setFolders(list || []))
      .finally(() => setLoadingFolders(false));
  }, [open]);

  const handleCreate = async () => {
    const rawName = form.name.trim();
    const description = form.description.trim();
    if (!rawName || !description || !form.folderId) return;
    setCreating(true);
    setError('');
    try {
      const knowledge = await KnowledgeApi.create({
        name: agenticSlug(rawName),
        displayName: rawName,
        description,
        folderId: form.folderId,
      });
      onCreated?.(knowledge);
      onClose();
    } catch (err) {
      setError(knowledgeCreateError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{I18n.t('AgenticChatFlow.CreateKnowledge')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box>
            <TextField
              label={I18n.t('AgenticChatFlow.Name')}
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              size="small"
              fullWidth
              autoFocus
              helperText={
                form.name.trim()
                  ? `${I18n.t('AgenticChatFlow.Slug')}: ${agenticSlug(form.name)}`
                  : undefined
              }
            />
          </Box>
          <Box>
            <TextField
              label={I18n.t('AgenticChatFlow.Description')}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              size="small"
              fullWidth
              multiline
              minRows={3}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 0.75,
              }}
            >
              {I18n.t('AgenticChatFlow.CreateKnowledgeDescriptionHelper')}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {!loadingFolders && folders.length === 0
              ? I18n.t('AgenticChatFlow.KnowledgeFolderEmpty')
              : I18n.t('AgenticChatFlow.KnowledgeFolderHelper')}
          </Typography>
          {!loadingFolders && folders.length > 0 && (
            <KnowledgeFolderSelector
              folderId={form.folderId}
              onFolderIdChange={folderId => setForm(prev => ({ ...prev, folderId }))}
              selectableFolders={folders}
            />
          )}
          {error ? (
            <Typography color="error" variant="body2" data-testid="create-knowledge-error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={creating} color="inherit">
          {I18n.t('AgenticChatFlow.Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={
            creating ||
            !form.name.trim() ||
            !form.description.trim() ||
            !form.folderId ||
            folders.length === 0
          }
        >
          {I18n.t('AgenticChatFlow.Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateKnowledgeDialog.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  onCreated: func,
};

export default CreateKnowledgeDialog;
